import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { 
  Box, Typography, Card, CardContent, Grid, 
  Button, Alert, CircularProgress, Divider,
  FormControl, InputLabel, Select, MenuItem,
  List, ListItem, ListItemText, ListItemIcon,
  Accordion, AccordionSummary, AccordionDetails,
  LinearProgress, Chip
} from '@mui/material';
import apiService from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupIcon from '@mui/icons-material/Group';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NodeDetail = () => {
  const { nodeId } = useParams();
  const [nodeData, setNodeData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [longHistoryData, setLongHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24); // Default to 24 hours

  useEffect(() => {
    const fetchNodeDetails = async () => {
      setLoading(true);
      try {
        // Fetch node details
        const nodeDetails = await apiService.getNodeDetails(nodeId);
        setNodeData(nodeDetails);
        
        // Fetch node recent history (last 10 minutes)
        const history = await apiService.getNodeHistory(nodeId);
        setHistoryData(history);
        
        // Fetch long-term history
        try {
          const longHistory = await apiService.getNodeLongHistory(nodeId, timeRange);
          setLongHistoryData(longHistory);
        } catch (err) {
          console.warn('Could not fetch long history data', err);
          // This is not critical, so we don't fail the entire request
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching node details:', err);
        setError(`Failed to fetch node details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchNodeDetails();
  }, [nodeId, timeRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  // Function to determine color based on utilization percentage
  const getUtilizationColor = (utilization) => {
    if (utilization < 30) return 'success';
    if (utilization < 70) return 'warning';
    return 'error';
  };

  const renderUtilizationChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No recent history available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'GPU Utilization %',
          data: historyData.history.map(entry => entry.avg_gpu_utilization),
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `${nodeId} - GPU Utilization (Last 10 minutes)`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Utilization %'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderMemoryChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No recent history available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'Memory Usage (MB)',
          data: historyData.history.map(entry => entry.avg_gpu_memory),
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `${nodeId} - Memory Usage (Last 10 minutes)`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Memory (MB)'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderProcessesChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No recent history available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'Number of Processes',
          data: historyData.history.map(entry => entry.num_processes),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `${nodeId} - Process Count (Last 10 minutes)`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Processes'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderLongTermUtilizationChart = () => {
    if (!longHistoryData || !longHistoryData.history || longHistoryData.history.length === 0) {
      return <Alert severity="info">No historical data available for this time range</Alert>;
    }

    const chartData = {
      labels: longHistoryData.history.map(entry => {
        const date = new Date(entry.timestamp_end);
        return date.toLocaleString();
      }),
      datasets: [
        {
          label: 'Avg GPU Utilization %',
          data: longHistoryData.history.map(entry => entry.avg_gpu_utilization),
          borderColor: 'rgba(153, 102, 255, 1)',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `${nodeId} - Long-term GPU Utilization`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Utilization %'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderActiveUsersSection = () => {
    if (!nodeData || !nodeData.active_users) {
      return <Alert severity="info">No user data available</Alert>;
    }

    const users = Array.isArray(nodeData.active_users) ? nodeData.active_users : [];

    return (
      <List dense>
        {users.length > 0 ? (
          users.map((user, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary={user} />
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText primary="No active users" />
          </ListItem>
        )}
      </List>
    );
  };

  const renderGPUs = () => {
    if (!nodeData || !nodeData.gpus) {
      return <Alert severity="info">No GPU data available</Alert>;
    }

    return (
      <Grid container spacing={2}>
        {nodeData.gpus.map((gpu) => (
          <Grid item xs={12} sm={6} md={4} key={`gpu-${gpu.gpu_index}`}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  GPU {gpu.gpu_index}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Utilization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {gpu.utilization}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={gpu.utilization} 
                    color={getUtilizationColor(gpu.utilization)}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
                
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Memory
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {gpu.memory_usage} MB
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(gpu.memory_usage / 32768) * 100} 
                    color="info"
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
                
                {gpu.temperature && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Temperature: {gpu.temperature}°C
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading && !nodeData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !nodeData) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!nodeData) {
    return <Alert severity="error">Node data not found</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            component={RouterLink} 
            to="/" 
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Node: {nodeId}
          </Typography>
        </Box>
        <Chip 
          label={nodeData.cluster_name} 
          color="secondary" 
          size="medium" 
        />
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Node Statistics</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="GPU Count" 
                    secondary={nodeData.gpu_count || nodeData.gpus?.length || 0} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Avg. GPU Utilization" 
                    secondary={`${Math.round(nodeData.avg_gpu_utilization || 0)}%`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Avg. GPU Memory" 
                    secondary={`${Math.round(nodeData.avg_gpu_memory || 0)} MB`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Processes" 
                    secondary={nodeData.num_processes} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Disk Usage</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total Space" 
                    secondary={nodeData.disk_usage?.total || 'N/A'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Used Space" 
                    secondary={nodeData.disk_usage?.used || 'N/A'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Free Space" 
                    secondary={nodeData.disk_usage?.free || 'N/A'} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Accordion defaultExpanded sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Active Users</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderActiveUsersSection()}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">GPUs on this Node</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderGPUs()}
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent GPU Utilization</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderUtilizationChart()
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Memory Usage</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderMemoryChart()
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recent Process Count</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderProcessesChart()
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Historical Data
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            id="time-range-select"
            value={timeRange}
            label="Time Range"
            onChange={handleTimeRangeChange}
          >
            <MenuItem value={1}>Last Hour</MenuItem>
            <MenuItem value={6}>Last 6 Hours</MenuItem>
            <MenuItem value={24}>Last 24 Hours</MenuItem>
            <MenuItem value={72}>Last 3 Days</MenuItem>
            <MenuItem value={168}>Last Week</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Long-term GPU Utilization</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderLongTermUtilizationChart()
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NodeDetail;